<template>
    <div class="ls-service-schedule-details--container">
        <div>
            <div class="ls-service-schedule-details--service">
                <LsServiceStatusIndicator :service="schedule" />
                <span
                    :class="[
                        'ls-service-schedule-details--service-name',
                        { 'add-padding': isPaw || isManualQuote || isQA || isManualPrice },
                    ]"
                    >{{ scheduleName }}</span
                >
                <LsServiceTypePill :service="schedule" />
            </div>
            <div v-if="contractorName" class="ls-service-schedule-details--pro">
                <a test-id="contractor" :href="oldAdminContractorLink" target="blank">{{
                    contractorName
                }}</a>
            </div>
            <div
                v-if="lookingForPro"
                class="ls-service-schedule-details--pro no-pro"
                test-id="no-pro"
            >
                <span>Looking for pro</span>
            </div>
        </div>
        <div v-if="isManualQuote || isManualPrice">
            <LsInfoRowQuotePricing
                :schedule="schedule"
                :quote="manualPriceQuote"
                :quote-loading="manualPriceQuoteLoading"
            />
        </div>
        <div v-else>
            <LsInfoRowPricing :schedule="schedule" />
        </div>
        <div v-if="isManualQuote || isManualPrice" test-id="status">
            <LsInfoRowStatus
                :service="schedule"
                :quote="manualPriceQuote"
                :quote-loading="manualPriceQuoteLoading"
            />
        </div>
        <div v-if="isManualQuote || isRequiredService || isManualPrice">
            <LsInfoRowQuoteCompleted
                :schedule="schedule"
                :quote="manualPriceQuote"
                :quote-loading="manualPriceQuoteLoading"
            />
        </div>
        <div v-else-if="schedule.last_completed_schedule_event || isQA || isPaw">
            <LsInfoRowLastCompleted :schedule="schedule" />
        </div>
        <div>
            <LsInfoRowUpcoming
                :schedule="schedule"
                :quote="manualPriceQuote"
                :quote-loading="manualPriceQuoteLoading"
            />
        </div>
        <div v-if="hasCycle">
            <LsInfoRowFrequency :schedule="schedule" />
        </div>
        <div v-if="isMowing">
            <LsInfoRowScope :schedule="schedule" :property="property" />
        </div>
        <div v-if="schedule.type !== 'service' || isManualPrice">
            <LsAdditionalDetails :schedule="schedule" :quote="manualPriceQuote" />
        </div>
        <div v-if="services.length > 1">
            <p class="ls-service-schedule-details--other-services">Other Services</p>
            <LsServiceList :services="services" :exclude="exclude" @click="handleClick" />
        </div>
    </div>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import { lsFilters, lsScheduleHelpers, lsPropTypeHelpers } from 'ls-vue-common';
import { lsHelpers } from '@/helpers';
import LsServiceStatusIndicator from '@/components/LsServiceStatusIndicator/LsServiceStatusIndicator.vue';
import LsAdditionalDetails from '@/components/LsAdditionalDetails/LsAdditionalDetails.vue';
import LsInfoRowPricing from '@/components/LsInfoRowPricing/LsInfoRowPricing.vue';
import LsInfoRowQuotePricing from '@/components/LsInfoRowQuotePricing/LsInfoRowQuotePricing.vue';
import LsInfoRowLastCompleted from '@/components/LsInfoRowLastCompleted/LsInfoRowLastCompleted.vue';
import LsInfoRowStatus from '@/components/LsInfoRowStatus/LsInfoRowStatus.vue';
import LsInfoRowScope from '@/components/LsInfoRowScope/LsInfoRowScope.vue';
import LsInfoRowUpcoming from '@/components/LsInfoRowUpcoming/LsInfoRowUpcoming.vue';
import LsInfoRowFrequency from '@/components/LsInfoRowFrequency/LsInfoRowFrequency.vue';
import LsInfoRowQuoteCompleted from '@/components/LsInfoRowQuoteCompleted/LsInfoRowQuoteCompleted.vue';
import LsServiceList from '@/components/LsServiceList/LsServiceList.vue';
import LsServiceTypePill from '@/components/LsServiceTypePill/LsServiceTypePill.vue';

const MANUAL_PRICE = 'manual_price';

export default {
    name: 'LsServiceScheduleDetails',
    components: {
        LsServiceStatusIndicator,
        LsAdditionalDetails,
        LsInfoRowPricing,
        LsInfoRowQuotePricing,
        LsInfoRowLastCompleted,
        LsInfoRowUpcoming,
        LsInfoRowFrequency,
        LsInfoRowQuoteCompleted,
        LsInfoRowStatus,
        LsInfoRowScope,
        LsServiceList,
        LsServiceTypePill,
    },
    props: {
        schedule: VueTypes.shape({
            __typename: VueTypes.oneOf(['Schedule', 'Recommendation', 'RequiredService'])
                .isRequired,
            id: VueTypes.string.isRequired,
            type: VueTypes.string,
            cycle: VueTypes.integer,
            created_at: VueTypes.string.isRequired,
            status: VueTypes.string,
            contractor: VueTypes.oneOfType([
                VueTypes.shape({
                    id: VueTypes.string.isRequired,
                    name: VueTypes.string.isRequired,
                    user: VueTypes.shape({
                        first_name: VueTypes.string.isRequired,
                        last_name: VueTypes.string.isRequired,
                    }).loose,
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            last_completed_schedule_event: VueTypes.custom(lsPropTypeHelpers.objectOrNull),
            last_schedule_cycle_change: VueTypes.custom(lsPropTypeHelpers.objectOrNull),
        }).loose,
        services: VueTypes.arrayOf(VueTypes.object).isRequired.loose,
        property: VueTypes.object.isRequired,
        manualPriceQuote: VueTypes.oneOfType([VueTypes.object, VueTypes.oneOfType([null])]).def(
            null
        ),
        manualPriceQuoteLoading: VueTypes.bool.def(false),
    },
    computed: {
        scheduleName() {
            return lsScheduleHelpers.getScheduleServiceName({
                schedule: this.schedule,
                nameOnly: !this.isQA,
            });
        },
        isMowing() {
            return lsHelpers.isMowing({ schedule: this.schedule });
        },
        isManualQuote() {
            const { type } = this.schedule;
            return type === 'manualquote';
        },
        isManualPrice() {
            return (
                !this.isManualQuote && _.get(this.schedule, 'service.price_type') === MANUAL_PRICE
            );
        },
        isPaw() {
            const { type } = this.schedule;
            return type === 'paw';
        },
        isQA() {
            const { type } = this.schedule;
            return type === 'qualityassurance';
        },
        isRequiredService() {
            return this.schedule.__typename === 'RequiredService';
        },
        contractorName() {
            if (_.isNil(this.schedule.contractor)) {
                return null;
            }

            const { user, name } = this.schedule.contractor;
            return _.toUpper(`${name} - ${lsFilters.fullName({ user })}`);
        },
        lookingForPro() {
            return (
                !this.contractorName ||
                (this.contractorName && this.schedule.next_eventstatus === 'pending')
            );
        },
        exclude() {
            return [
                {
                    __typename: this.schedule.__typename,
                    id: this.schedule.id,
                },
            ];
        },
        oldAdminContractorLink() {
            return `${process.env.VUE_APP_ADMIN_BASE_URL}/contractors/${this.schedule.contractor.id}`;
        },
        hasCycle() {
            return _.has(this.schedule, 'cycle');
        },
    },
    methods: {
        handleClick({ id, __typename }) {
            const routeName = _.camelCase(__typename);

            this.$router.push({
                name: routeName,
                params: {
                    customer_id: this.$route.params.customer_id,
                    property_id: this.$route.params.property_id,
                    [`${routeName}_id`]: id,
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.ls-service-schedule-details--container {
    padding: 30px 20px;
    border-radius: $border-radius;
    background-color: $white;
    display: flex;
    flex-direction: column;

    > div {
        padding: 15px 0;
        border-bottom: 1px solid $secondary-light;
    }

    div:first-child {
        padding-top: 0;
    }

    div:last-child {
        border: none;
        padding-bottom: 0;
    }
}

.ls-service-schedule-details--service {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
}

.ls-service-schedule-details--service-name {
    font-weight: $bold;
    padding: 1px 0 0 10px;
    color: $secondary;

    &.add-padding {
        padding-right: 8px;
    }
}

.ls-service-schedule-details--pro {
    padding-left: 22px;

    &.no-pro {
        color: $gray-primary;
    }
}

.ls-service-schedule-details--other-services {
    font-weight: $bold;
    color: $secondary;
    margin-bottom: 10px;
}
</style>
